import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';

import useCobrandingPartnership from '../hooks/useCobrandingPartnership';

import { Logo } from '@sayido/ui';
import CreateAccountButton from '../components/CreateAccountButton';

const TOP_OFFSET = 120;
const MOBILE_BREAKPOINT = 740;

const linkStyles = css`
  padding: 1.5rem;
  font-size: 16px;
  flex:1;
  white-space: nowrap;
  text-align: center;
  color:rgb(110, 110, 110);
  position: relative;
  display: block;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  ${p =>
    p.primary
      ? `background: var(--primary-500);padding: 1.5rem 2rem;color:#fff !important;opacity:1!important;border-radius: 4px;margin-left: 12px;font-weight: bold; &:hover {background: var(--primary-500);box-shadow:0px 3px 16px 0px rgba(62, 57, 107, 0.3);transform:translateY(-1px)}`
      : ``}

    @media(max-width: ${MOBILE_BREAKPOINT + 200}px) {
      padding: 1.5rem 0.8rem;
    }
  `;

const NavLink = styled('a')`
  ${linkStyles}
`;

const InternalLink = styled(Link)`
  ${linkStyles}
`;

const Wrapper = styled.nav`
  box-shadow: ${p =>
    p.state === 'sticky'
      ? '0px 9px 68px 0px rgba(62, 57, 107, 0.3)'
      : '0px 9px 68px 0px rgba(62, 57, 107, 0.1)'};
  background: ${p =>
    p.state === 'sticky' ? 'rgba(255,255,255,.97)' : '#fff !important'};
  box-sizing: border-box;
  padding: ${p => (p.state === 'sticky' ? '8px 20px' : '20px')};
  position: fixed;
  width: 100vw;
  top: 0;
  transition: all 0.3s ease;
  z-index: 50;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0px 20px;
  }

  & ${NavLink}, & ${InternalLink} {
    opacity: ${p => (p.state === 'sticky' ? '.6' : '1')};
  }

  &:hover ${NavLink}, &:hover ${InternalLink} {
    opacity: 1;
  }
`;

const Inner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1140px;
  margin: 0 auto;
  flex-wrap: nowrap;
  padding: 0 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0;
  }

  .nav-logo {
    width: 150px;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      width: 105px;
    }
  }
`;

const Collapse = styled.div`
  display: flex;
  flex-grow: 1;
  flex-basis: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: ${p => (p.open ? 'flex' : 'none')};
    position: absolute;
    z-index: 100;
    top: 100%;
    left: 0;
    width: 100%;
    background: #fff;
    padding: 40px 0;
    box-shadow: 0px 25px 40px 0px rgba(62, 57, 107, 0.1);

    &:after {
      // content: " ";
      background: #fff;
      bottom: 0px;
      height: 20px;
      box-shadow: 0px 9px 68px 0px rgba(62, 57, 107, 0.3);
      width: 100%;
      position: absolute;
      left: 0;
      z-index: -1;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: row;
  margin: 0;
  margin-left: auto !important;
  padding: 0;
  list-style: none;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    justify-content: stretch;
    width: auto;
    margin: 0 auto;
  }
`;

const MenuBtn = styled.button`
  border: none;
  background: transparent;
  height: 54px;
  width: 54px;
  position: relative;

  @media (min-width: ${MOBILE_BREAKPOINT - 1}px) {
    display: none;
  }

  &:before {
    content: ' ';
    border-bottom: solid 2px #444;
    width: 24px;
    height: 0;
    transform: ${p => (p.open ? 'rotate(45deg) translate(3%,-152%)' : '')};
    position: absolute;
    top: 46%;
    transform-origin: center;
  }
  &:after {
    border-bottom: ${p => (!p.open ? 'solid 2px #444' : 'none')};
    border-top: solid 2px #444;
    width: 24px;
    height: 14px;
    content: ' ';
    top: 32%;
    transform: ${p => (p.open ? 'rotate(-45deg) translate(12%,48%)' : '')};
    position: absolute;
    transform-origin: center;
  }

  &:focus {
    outline: none;
  }
`;

const NavMenuContainer = styled.div`
  display: flex;
`;

const MobileRegisterCTA = styled.div`
  margin: auto;

  > div {
    padding: 6px 12px;
    font-size: 13px;
    opacity: ${p => (p.isSticky ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;

    @media (min-width: ${MOBILE_BREAKPOINT}px) {
      display: none;
    }
  }
`;

const NavItem = styled.li`
  flex: 1;
`;

const CobrandLogo = styled.img`
  width: 200px;
`;

export default function Nav({ showNavLinks }) {
  const cobranding = useCobrandingPartnership();
  const [isSticky, setIsSticky] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const update = () => {
      if (window.scrollY > TOP_OFFSET && !isSticky) {
        setIsSticky(true);
      }
      if (window.scrollY < TOP_OFFSET && !!isSticky) {
        setIsSticky(false);
      }
    };

    document.addEventListener('scroll', update, {
      passive: true,
    });

    update();

    return () => {
      document.removeEventListener('scroll', update);
    };
  }, [isSticky]);

  return (
    <Wrapper state={!!isSticky ? 'sticky' : 'regular'}>
      <Inner>
        <Link to="/">
          {cobranding.hasPartnership ? (
            <div
              style={{
                textAlign: 'center',
                fontSize: '32px',
                lineHeight: '25px',
              }}
            >
              <CobrandLogo src={cobranding.logo} />
            </div>
          ) : (
            <Logo className="nav-logo" alt="Say I do" size="150" />
          )}
        </Link>
        <NavMenuContainer>
          <MobileRegisterCTA isSticky={isSticky}>
            <CreateAccountButton location={'nav'} type="primary" />
          </MobileRegisterCTA>
          <MenuBtn
            open={isOpen}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          />
        </NavMenuContainer>
        <Collapse open={isOpen}>
          <NavList>
            {showNavLinks && (
              <React.Fragment>
                <NavItem>
                  <InternalLink to={'/wedding-website/designs'}>
                    Designs
                  </InternalLink>
                </NavItem>
                <NavItem>
                  <InternalLink to={'/features'}>Features</InternalLink>
                </NavItem>
                <NavItem>
                  <InternalLink to={'/pricing'}>Pricing</InternalLink>
                </NavItem>
                <NavItem>
                  <InternalLink to={'/faq'}>FAQ</InternalLink>
                </NavItem>
                <NavItem>
                  <NavLink href="https://help.sayi.do" target="_blank">
                    Help
                  </NavLink>
                </NavItem>
              </React.Fragment>
            )}
            <NavItem>
              <NavLink href={`/login`}>Sign In</NavLink>
            </NavItem>
            <NavItem>
              <CreateAccountButton location={'nav'} primary={true} />
            </NavItem>
          </NavList>
        </Collapse>
      </Inner>
    </Wrapper>
  );
}
